import { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

import { API_BASE_URL as API } from '../utils/apiConfig';

const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  isSuperAdmin: false,
  brand: null,
  allowedSections: [],
  login: () => {},
  logout: () => {},
  checkAuth: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking
  const [brand, setBrand] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      // Create a timeout promise
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await axios.get(`${API}/api/auth/me`, {
        withCredentials: true,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const userData = response.data;
      setUser(userData);
      setIsAuthenticated(true);
      
      // Store user info in localStorage for use by other contexts
      if (userData.is_super_admin) {
        localStorage.setItem('isSuperAdmin', 'true');
      } else {
        localStorage.setItem('isSuperAdmin', 'false');
      }
      
      if (userData.brand_id) {
        localStorage.setItem('userBrandId', userData.brand_id.toString());
      }
      
      // Set brand if available from auth response
      if (userData.brand) {
        setBrand(userData.brand);
      }
      
      return userData;
    } catch (error) {
      console.log('Auth check error:', error?.message || 'Unknown error');
      // Clear localStorage on auth failure
      localStorage.removeItem('isSuperAdmin');
      localStorage.removeItem('userBrandId');
      localStorage.removeItem('selectedBrandId');
      
      setUser(null);
      setIsAuthenticated(false);
      setBrand(null);
      return null;
    }
  }, []);

  const login = useCallback((userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    
    // Store user info in localStorage for use by other contexts
    if (userData.is_super_admin) {
      localStorage.setItem('isSuperAdmin', 'true');
    } else {
      localStorage.setItem('isSuperAdmin', 'false');
    }
    
    if (userData.brand_id) {
      localStorage.setItem('userBrandId', userData.brand_id.toString());
    }
    
    if (userData.brand) {
      setBrand(userData.brand);
    }
  }, []);

  const logout = useCallback(async () => {
    // Clear state first to prevent any auto-redirects
    setUser(null);
    setIsAuthenticated(false);
    setBrand(null);
    
    // Clear localStorage
    localStorage.removeItem('isSuperAdmin');
    localStorage.removeItem('userBrandId');
    localStorage.removeItem('selectedBrandId');
    
    // Set a flag in sessionStorage to prevent auto-redirect loops
    sessionStorage.setItem('just_logged_out', 'true');
    
    // Call server logout
    try {
      await axios.post(`${API}/api/auth/logout`, {}, {
        withCredentials: true,
        timeout: 3000,
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Force redirect to login page directly
    window.location.replace('/login');
  }, []);

  // Computed values
  const isSuperAdmin = user?.is_super_admin || false;
  const allowedSections = user?.allowed_sections || [];
  const userRole = user?.role || null;
  const brandId = user?.brand_id || brand?.brand_id || null;

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isSuperAdmin,
      userRole,
      brandId,
      brand, 
      setBrand,
      allowedSections,
      login, 
      logout, 
      checkAuth 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
